<template>
  <div class="dashboard-container d-flex">
    <!-- Sidebar -->
    <AdminSidebar :sidebarOpen="sidebarOpen" currentPage="timesheets" />

    <!-- Main Content -->
    <div
      class="main-content flex-grow-1"
      :class="{ 'sidebar-collapsed': !sidebarOpen }"
    >
      <!-- Top Navbar -->
      <AdminNavbar 
        :title="$t('timesheets.title')" 
        @toggleSidebar="toggleSidebar" 
        @logout="handleLogout" 
      />

      <!-- Content -->
      <div class="container-fluid p-4">
        <!-- Statistics Cards -->
        <div class="row mb-4">
          <div class="col-md-4 mb-3">
            <div class="card border-0 shadow-sm stat-card stat-warning">
              <div class="card-body">
                <div class="d-flex justify-content-between align-items-center">
                  <div>
                    <p class="text-muted mb-1 small">{{ $t('timesheets.pendingTimesheets') }}</p>
                    <h3 class="mb-0 fw-bold">{{ pendingCount }}</h3>
                  </div>
                  <div class="stat-icon bg-warning">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      fill="currentColor"
                      class="bi bi-hourglass-split"
                      viewBox="0 0 16 16"
                    >
                      <path
                        d="M2.5 15a.5.5 0 1 1 0-1h1v-1a4.5 4.5 0 0 1 2.557-4.06c.29-.139.443-.377.443-.59v-.7c0-.213-.154-.451-.443-.59A4.5 4.5 0 0 1 3.5 3V2h-1a.5.5 0 0 1 0-1h11a.5.5 0 0 1 0 1h-1v1a4.5 4.5 0 0 1-2.557 4.06c-.29.139-.443.377-.443.59v.7c0 .213.154.451.443.59A4.5 4.5 0 0 1 12.5 13v1h1a.5.5 0 0 1 0 1h-11zm2-13v1c0 .537.12 1.045.337 1.5h6.326c.216-.455.337-.963.337-1.5V2h-7zm3 6.35c0 .701-.478 1.236-1.011 1.492A3.5 3.5 0 0 0 4.5 13s.866-1.299 3-1.48V8.35zm1 0v3.17c2.134.181 3 1.48 3 1.48a3.5 3.5 0 0 0-1.989-3.158C8.978 9.586 8.5 9.052 8.5 8.351z"
                      />
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="col-md-4 mb-3">
            <div class="card border-0 shadow-sm stat-card stat-success">
              <div class="card-body">
                <div class="d-flex justify-content-between align-items-center">
                  <div>
                    <p class="text-muted mb-1 small">{{ $t('timesheets.approvedThisWeek') }}</p>
                    <h3 class="mb-0 fw-bold">
                      {{ approvedThisWeekCount }}
                      <small class="text-muted fs-6"
                        >({{ approvedHours }}h)</small
                      >
                    </h3>
                  </div>
                  <div class="stat-icon bg-success">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      fill="currentColor"
                      class="bi bi-check-circle-fill"
                      viewBox="0 0 16 16"
                    >
                      <path
                        d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zm-3.97-3.03a.75.75 0 0 0-1.08.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-.01-1.05z"
                      />
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="col-md-4 mb-3">
            <div class="card border-0 shadow-sm stat-card stat-info">
              <div class="card-body">
                <div class="d-flex justify-content-between align-items-center">
                  <div>
                    <p class="text-muted mb-1 small">{{ $t('timesheets.awaitingPayroll') }}</p>
                    <h3 class="mb-0 fw-bold">
                      {{ awaitingPayrollCount }}
                      <small class="text-muted fs-6"
                        >({{ awaitingPayrollHours }}h)</small
                      >
                    </h3>
                  </div>
                  <div class="stat-icon bg-info">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      fill="currentColor"
                      class="bi bi-currency-euro"
                      viewBox="0 0 16 16"
                    >
                      <path
                        d="M4 9.42h1.063C5.4 12.323 7.317 14 10.34 14c.622 0 1.167-.068 1.659-.185v-1.3c-.484.119-1.045.17-1.659.17-2.1 0-3.455-1.198-3.775-3.264h4.017v-.928H6.497v-.936c0-.11 0-.219.008-.329h4.078v-.927H6.618c.388-1.898 1.719-2.985 3.723-2.985.614 0 1.175.05 1.659.177V2.194A6.617 6.617 0 0 0 10.341 2c-3.023 0-4.941 1.676-5.277 4.42H4v.928h1.053v1.265H4v.927z"
                      />
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Toolbar -->
        <div class="row mb-4">
          <div class="col-md-4 mb-3 mb-md-0">
            <div class="input-group">
              <span class="input-group-text bg-white">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  fill="currentColor"
                  class="bi bi-search"
                  viewBox="0 0 16 16"
                >
                  <path
                    d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001c.03.04.062.078.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1.007 1.007 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0z"
                  />
                </svg>
              </span>
              <input
                type="text"
                class="form-control"
                :placeholder="$t('timesheets.searchPlaceholder')"
                v-model="searchQuery"
              />
            </div>
          </div>
          <div class="col-md-3 mb-3 mb-md-0">
            <select class="form-select" v-model="statusFilter">
              <option value="all">{{ $t('common.allStatus') }}</option>
              <option value="pending">{{ $t('timesheets.pending') }}</option>
              <option value="approved">{{ $t('timesheets.approved') }}</option>
              <option value="paid">{{ $t('timesheets.paid') }}</option>
            </select>
          </div>
          <div class="col-md-5 text-end">
            <button
              class="btn btn-success me-2"
              @click="bulkApprove"
              :disabled="selectedTimesheets.length === 0"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                fill="currentColor"
                class="bi bi-check-circle"
                viewBox="0 0 16 16"
              >
                <path
                  d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z"
                />
                <path
                  d="M10.97 4.97a.235.235 0 0 0-.02.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-1.071-1.05z"
                />
              </svg>
              {{ $t('timesheets.bulkApprove', { count: selectedTimesheets.length }) }}
            </button>
          </div>
        </div>

        <!-- Timesheets Table -->
        <div class="card border-0 shadow-sm">
          <div class="card-body p-0">
            <div class="table-responsive">
              <table class="table table-hover mb-0">
                <thead class="table-light">
                  <tr>
                    <th width="40">
                      <input
                        type="checkbox"
                        class="form-check-input"
                        @change="toggleSelectAll"
                        :checked="isAllSelected"
                      />
                    </th>
                    <th @click="sortBy('employeeName')" class="sortable">
                      {{ $t('timesheets.employee') }}
                      <svg
                        v-if="sortField === 'employeeName'"
                        xmlns="http://www.w3.org/2000/svg"
                        width="12"
                        height="12"
                        fill="currentColor"
                        :class="{
                          'bi-arrow-up': sortDirection === 'asc',
                          'bi-arrow-down': sortDirection === 'desc',
                        }"
                        viewBox="0 0 16 16"
                      >
                        <path
                          v-if="sortDirection === 'asc'"
                          fill-rule="evenodd"
                          d="M8 15a.5.5 0 0 0 .5-.5V2.707l3.146 3.147a.5.5 0 0 0 .708-.708l-4-4a.5.5 0 0 0-.708 0l-4 4a.5.5 0 1 0 .708.708L7.5 2.707V14.5a.5.5 0 0 0 .5.5z"
                        />
                        <path
                          v-else
                          fill-rule="evenodd"
                          d="M8 1a.5.5 0 0 1 .5.5v11.793l3.146-3.147a.5.5 0 0 1 .708.708l-4 4a.5.5 0 0 1-.708 0l-4-4a.5.5 0 0 1 .708-.708L7.5 13.293V1.5A.5.5 0 0 1 8 1z"
                        />
                      </svg>
                    </th>
                    <th @click="sortBy('weekStart')" class="sortable">
                      {{ $t('timesheets.week') }}
                      <svg
                        v-if="sortField === 'weekStart'"
                        xmlns="http://www.w3.org/2000/svg"
                        width="12"
                        height="12"
                        fill="currentColor"
                        :class="{
                          'bi-arrow-up': sortDirection === 'asc',
                          'bi-arrow-down': sortDirection === 'desc',
                        }"
                        viewBox="0 0 16 16"
                      >
                        <path
                          v-if="sortDirection === 'asc'"
                          fill-rule="evenodd"
                          d="M8 15a.5.5 0 0 0 .5-.5V2.707l3.146 3.147a.5.5 0 0 0 .708-.708l-4-4a.5.5 0 0 0-.708 0l-4 4a.5.5 0 1 0 .708.708L7.5 2.707V14.5a.5.5 0 0 0 .5.5z"
                        />
                        <path
                          v-else
                          fill-rule="evenodd"
                          d="M8 1a.5.5 0 0 1 .5.5v11.793l3.146-3.147a.5.5 0 0 1 .708.708l-4 4a.5.5 0 0 1-.708 0l-4-4a.5.5 0 0 1 .708-.708L7.5 13.293V1.5A.5.5 0 0 1 8 1z"
                        />
                      </svg>
                    </th>
                    <th @click="sortBy('totalHours')" class="sortable">
                      {{ $t('timesheets.totalHours') }}
                      <svg
                        v-if="sortField === 'totalHours'"
                        xmlns="http://www.w3.org/2000/svg"
                        width="12"
                        height="12"
                        fill="currentColor"
                        :class="{
                          'bi-arrow-up': sortDirection === 'asc',
                          'bi-arrow-down': sortDirection === 'desc',
                        }"
                        viewBox="0 0 16 16"
                      >
                        <path
                          v-if="sortDirection === 'asc'"
                          fill-rule="evenodd"
                          d="M8 15a.5.5 0 0 0 .5-.5V2.707l3.146 3.147a.5.5 0 0 0 .708-.708l-4-4a.5.5 0 0 0-.708 0l-4 4a.5.5 0 1 0 .708.708L7.5 2.707V14.5a.5.5 0 0 0 .5.5z"
                        />
                        <path
                          v-else
                          fill-rule="evenodd"
                          d="M8 1a.5.5 0 0 1 .5.5v11.793l3.146-3.147a.5.5 0 0 1 .708.708l-4 4a.5.5 0 0 1-.708 0l-4-4a.5.5 0 0 1 .708-.708L7.5 13.293V1.5A.5.5 0 0 1 8 1z"
                        />
                      </svg>
                    </th>
                    <th>{{ $t('common.status') }}</th>
                    <th @click="sortBy('submittedDate')" class="sortable">
                      {{ $t('timesheets.submitted') }}
                      <svg
                        v-if="sortField === 'submittedDate'"
                        xmlns="http://www.w3.org/2000/svg"
                        width="12"
                        height="12"
                        fill="currentColor"
                        :class="{
                          'bi-arrow-up': sortDirection === 'asc',
                          'bi-arrow-down': sortDirection === 'desc',
                        }"
                        viewBox="0 0 16 16"
                      >
                        <path
                          v-if="sortDirection === 'asc'"
                          fill-rule="evenodd"
                          d="M8 15a.5.5 0 0 0 .5-.5V2.707l3.146 3.147a.5.5 0 0 0 .708-.708l-4-4a.5.5 0 0 0-.708 0l-4 4a.5.5 0 1 0 .708.708L7.5 2.707V14.5a.5.5 0 0 0 .5.5z"
                        />
                        <path
                          v-else
                          fill-rule="evenodd"
                          d="M8 1a.5.5 0 0 1 .5.5v11.793l3.146-3.147a.5.5 0 0 1 .708.708l-4 4a.5.5 0 0 1-.708 0l-4-4a.5.5 0 0 1 .708-.708L7.5 13.293V1.5A.5.5 0 0 1 8 1z"
                        />
                      </svg>
                    </th>
                    <th class="text-end">{{ $t('common.actions') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="loading">
                    <td colspan="7" class="text-center py-4">
                      <div class="spinner-border text-primary" role="status">
                        <span class="visually-hidden">{{ $t('common.loading') }}</span>
                      </div>
                    </td>
                  </tr>
                  <tr v-else-if="error">
                    <td colspan="7" class="text-center py-4">
                      <div class="alert alert-danger mb-0">{{ error }}</div>
                    </td>
                  </tr>
                  <tr v-else-if="filteredTimesheets.length === 0">
                    <td colspan="7" class="text-center py-4 text-muted">
                      {{ $t('timesheets.noTimesheetsFound') }}
                    </td>
                  </tr>
                  <tr
                    v-else
                    v-for="timesheet in filteredTimesheets"
                    :key="timesheet.id"
                  >
                    <td>
                      <input
                        type="checkbox"
                        class="form-check-input"
                        :checked="selectedTimesheets.includes(timesheet.id)"
                        @change="toggleSelect(timesheet.id)"
                        :disabled="timesheet.status !== 'pending'"
                      />
                    </td>
                    <td class="fw-semibold">{{ timesheet.employee_name }}</td>
                    <td>
                      {{ formatWeek(timesheet.week_start_date, timesheet.week_end_date) }}
                    </td>
                    <td>{{ timesheet.total_hours }}h</td>
                    <td>
                      <span
                        class="badge"
                        :class="getStatusBadgeClass(timesheet.status)"
                      >
                        {{ $t('timesheets.' + timesheet.status) }}
                      </span>
                      <!-- How many days this week's approve/reject would act on -->
                      <div v-if="timesheet.pending_days" class="small text-muted mt-1">
                        {{ $t('timesheets.pendingDays', { count: timesheet.pending_days }) }}
                      </div>
                    </td>
                    <td>{{ formatDate(timesheet.submitted_at) }}</td>
                    <td class="text-end">
                      <button
                        class="btn btn-sm btn-outline-primary me-2"
                        @click="viewTimesheet(timesheet)"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="14"
                          height="14"
                          fill="currentColor"
                          class="bi bi-eye"
                          viewBox="0 0 16 16"
                        >
                          <path
                            d="M16 8s-3-5.5-8-5.5S0 8 0 8s3 5.5 8 5.5S16 8 16 8zM1.173 8a13.133 13.133 0 0 1 1.66-2.043C4.12 4.668 5.88 3.5 8 3.5c2.12 0 3.879 1.168 5.168 2.457A13.133 13.133 0 0 1 14.828 8c-.058.087-.122.183-.195.288-.335.48-.83 1.12-1.465 1.755C11.879 11.332 10.119 12.5 8 12.5c-2.12 0-3.879-1.168-5.168-2.457A13.134 13.134 0 0 1 1.172 8z"
                          />
                          <path
                            d="M8 5.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5zM4.5 8a3.5 3.5 0 1 1 7 0 3.5 3.5 0 0 1-7 0z"
                          />
                        </svg>
                      </button>
                      <button
                        v-if="timesheet.status === 'pending'"
                        class="btn btn-sm btn-success me-2"
                        @click="approveTimesheet(timesheet.id)"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="14"
                          height="14"
                          fill="currentColor"
                          class="bi bi-check-lg"
                          viewBox="0 0 16 16"
                        >
                          <path
                            d="M12.736 3.97a.733.733 0 0 1 1.047 0c.286.289.29.756.01 1.05L7.88 12.01a.733.733 0 0 1-1.065.02L3.217 8.384a.757.757 0 0 1 0-1.06.733.733 0 0 1 1.047 0l3.052 3.093 5.4-6.425a.247.247 0 0 1 .02-.022Z"
                          />
                        </svg>
                      </button>
                      <button
                        v-if="timesheet.status === 'pending'"
                        class="btn btn-sm btn-outline-danger"
                        @click="openRejectModal(timesheet)"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="14"
                          height="14"
                          fill="currentColor"
                          class="bi bi-x-lg"
                          viewBox="0 0 16 16"
                        >
                          <path
                            d="M2.146 2.854a.5.5 0 1 1 .708-.708L8 7.293l5.146-5.147a.5.5 0 0 1 .708.708L8.707 8l5.147 5.146a.5.5 0 0 1-.708.708L8 8.707l-5.146 5.147a.5.5 0 0 1-.708-.708L7.293 8 2.146 2.854Z"
                          />
                        </svg>
                      </button>
                    </td>
                  </tr>
                  <tr v-if="paginatedTimesheets.length === 0">
                    <td colspan="7" class="text-center py-4 text-muted">
                      No timesheets found
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <!-- Pagination -->
        <div class="row mt-3" v-if="pagination.totalPages > 1">
          <div class="col">
            <nav>
              <ul class="pagination justify-content-center mb-0">
                <li class="page-item" :class="{ disabled: pagination.currentPage === 1 }">
                  <a class="page-link" href="#" @click.prevent="changePage(pagination.currentPage - 1)"
                    >{{ $t('common.previous') }}</a
                  >
                </li>
                <li
                  v-for="page in pagination.totalPages"
                  :key="page"
                  class="page-item"
                  :class="{ active: pagination.currentPage === page }"
                >
                  <a
                    class="page-link"
                    href="#"
                    @click.prevent="changePage(page)"
                    >{{ page }}</a
                  >
                </li>
                <li
                  class="page-item"
                  :class="{ disabled: pagination.currentPage === pagination.totalPages }"
                >
                  <a class="page-link" href="#" @click.prevent="changePage(pagination.currentPage + 1)"
                    >{{ $t('common.next') }}</a
                  >
                </li>
              </ul>
            </nav>
          </div>
        </div>
      </div>
    </div>

    <!-- Details Modal -->
    <div
      class="modal fade"
      :class="{ show: showModal, 'd-block': showModal }"
      tabindex="-1"
      v-if="showModal"
    >
      <div class="modal-dialog modal-dialog-centered modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">{{ $t('timesheets.timesheetDetails') }}</h5>
            <button
              type="button"
              class="btn-close"
              @click="closeModal"
            ></button>
          </div>
          <div class="modal-body" v-if="selectedTimesheet">
            <div class="row g-3 mb-3">
              <div class="col-md-6">
                <div class="detail-label">{{ $t('timesheets.employee') }}</div>
                <div class="detail-value">{{ selectedTimesheet.employee_name }}</div>
              </div>
              <div class="col-md-6">
                <div class="detail-label">{{ $t('timesheets.week') }}</div>
                <div class="detail-value">
                  {{
                    formatWeek(
                      selectedTimesheet.week_start_date,
                      selectedTimesheet.week_end_date
                    )
                  }}
                </div>
              </div>
              <div class="col-md-6">
                <div class="detail-label">{{ $t('timesheets.totalHours') }}</div>
                <div class="detail-value">{{ selectedTimesheet.total_hours }}{{ $t('timesheets.hours') }}</div>
              </div>
              <div class="col-md-6">
                <div class="detail-label">{{ $t('common.status') }}</div>
                <div class="detail-value">
                  <span
                    class="badge"
                    :class="getStatusBadgeClass(selectedTimesheet.status)"
                  >
                    {{ $t('timesheets.' + selectedTimesheet.status) }}
                  </span>
                </div>
              </div>
              <div class="col-md-6">
                <div class="detail-label">{{ $t('timesheets.submitted') }}</div>
                <div class="detail-value">{{ formatDate(selectedTimesheet.submitted_at) }}</div>
              </div>
              <div class="col-md-6" v-if="selectedTimesheet.approved_at">
                <div class="detail-label">{{ $t('timesheets.approved') }}</div>
                <div class="detail-value">{{ formatDate(selectedTimesheet.approved_at) }}</div>
              </div>
            </div>

            <div
              v-if="selectedTimesheet.rejection_reason"
              class="alert alert-danger py-2 px-3 mb-3"
            >
              <strong>{{ $t('timesheets.rejectionReason') }}:</strong>
              {{ selectedTimesheet.rejection_reason }}
            </div>

            <hr />

            <h6 class="mb-3">{{ $t('timesheets.dailyBreakdown') }}</h6>
            <div class="table-responsive">
              <table class="table table-sm align-middle mb-0 breakdown-table">
                <thead class="table-light">
                  <tr>
                    <th>{{ $t('timesheets.day') }}</th>
                    <th>{{ $t('timesheets.date') }}</th>
                    <th class="text-end">{{ $t('timesheets.hoursLabel') }}</th>
                    <th>{{ $t('timesheets.dayStatus') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="day in dailyBreakdown"
                    :key="day.key"
                    :class="{ 'text-muted': !day.hours }"
                  >
                    <td>{{ $t('timesheets.' + day.key) }}</td>
                    <td>{{ formatDate(day.date) }}</td>
                    <td class="text-end">
                      {{ day.hours ? day.hours + $t('timesheets.hours') : '—' }}
                    </td>
                    <td>
                      <span
                        v-if="day.status"
                        class="badge"
                        :class="getStatusBadgeClass(day.status)"
                      >
                        {{ $t('timesheets.' + day.status) }}
                      </span>
                      <span v-else>—</span>
                      <div
                        v-if="day.rejectionReason"
                        class="small text-danger mt-1"
                      >
                        {{ day.rejectionReason }}
                      </div>
                    </td>
                  </tr>
                </tbody>
                <tfoot class="table-light fw-bold">
                  <tr>
                    <td colspan="2">{{ $t('timesheets.total') }}</td>
                    <td class="text-end">
                      {{ selectedTimesheet.total_hours }}{{ $t('timesheets.hours') }}
                    </td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>

            <!-- Where the week's hours stand across the day statuses -->
            <div class="row g-3 mt-1">
              <div class="col-6 col-md-3">
                <div class="detail-label">{{ $t('timesheets.pendingHours') }}</div>
                <div class="detail-value">
                  {{ selectedTimesheet.pending_hours || 0 }}{{ $t('timesheets.hours') }}
                </div>
              </div>
              <div class="col-6 col-md-3">
                <div class="detail-label">{{ $t('timesheets.approvedHours') }}</div>
                <div class="detail-value">
                  {{ selectedTimesheet.approved_hours || 0 }}{{ $t('timesheets.hours') }}
                </div>
              </div>
              <div class="col-6 col-md-3">
                <div class="detail-label">{{ $t('timesheets.paidHours') }}</div>
                <div class="detail-value">
                  {{ selectedTimesheet.paid_hours || 0 }}{{ $t('timesheets.hours') }}
                </div>
              </div>
              <div class="col-6 col-md-3">
                <div class="detail-label">{{ $t('timesheets.payableHours') }}</div>
                <div class="detail-value">
                  {{ selectedTimesheet.payable_hours || 0 }}{{ $t('timesheets.hours') }}
                </div>
              </div>
            </div>

            <div v-if="selectedTimesheet.notes" class="mt-3">
              <div class="detail-label">{{ $t('timesheets.notes') }}</div>
              <p class="mb-0">{{ selectedTimesheet.notes }}</p>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="closeModal">
              {{ $t('common.close') }}
            </button>
            <button
              v-if="pendingDayCount > 0"
              type="button"
              class="btn btn-danger"
              @click="openRejectModal(selectedTimesheet)"
            >
              {{ $t('timesheets.rejectDays', { count: pendingDayCount }) }}
            </button>
            <button
              v-if="pendingDayCount > 0"
              type="button"
              class="btn btn-success"
              @click="approveTimesheet(selectedTimesheet.id); closeModal();"
            >
              {{ $t('timesheets.approveDays', { count: pendingDayCount }) }}
            </button>
          </div>
        </div>
      </div>
    </div>
    <div
      class="modal-backdrop fade"
      :class="{ show: showModal }"
      v-if="showModal"
    ></div>

    <!-- Reject Modal -->
    <div
      class="modal fade"
      :class="{ show: showRejectModal, 'd-block': showRejectModal }"
      tabindex="-1"
      v-if="showRejectModal"
    >
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">{{ $t('timesheets.rejectTimesheet') }}</h5>
            <button
              type="button"
              class="btn-close"
              @click="closeRejectModal"
            ></button>
          </div>
          <div class="modal-body">
            <p>
              {{
                pendingDayCount > 0
                  ? $t('timesheets.rejectConfirmationDays', {
                      count: pendingDayCount
                    })
                  : $t('timesheets.rejectConfirmation')
              }}
            </p>
            <div class="mb-3">
              <label class="form-label">{{ $t('timesheets.rejectionReason') }} <span class="text-danger">*</span></label>
              <textarea
                class="form-control"
                rows="3"
                v-model="rejectionReason"
                :placeholder="$t('timesheets.rejectionReasonPlaceholder')"
                required
              ></textarea>
            </div>
          </div>
          <div class="modal-footer">
            <button
              type="button"
              class="btn btn-secondary"
              @click="closeRejectModal"
            >
              {{ $t('common.cancel') }}
            </button>
            <button
              type="button"
              class="btn btn-danger"
              @click="confirmReject"
            >
              {{ $t('timesheets.rejectTimesheet') }}
            </button>
          </div>
        </div>
      </div>
    </div>
    <div
      class="modal-backdrop fade"
      :class="{ show: showRejectModal }"
      v-if="showRejectModal"
    ></div>
  </div>
</template>

<script>
import axios from 'axios';
import AdminSidebar from "@/components/AdminSidebar.vue";
import AdminNavbar from "@/components/AdminNavbar.vue";

const API_URL = process.env.VUE_APP_API_URL || "http://localhost:4000";

export default {
  name: "Timesheets",
  components: {
    AdminSidebar,
    AdminNavbar,
  },
  data() {
    return {
      sidebarOpen: true,
      searchQuery: "",
      statusFilter: "all",
      sortField: "submitted_at",
      sortDirection: "desc",
      showModal: false,
      showRejectModal: false,
      selectedTimesheet: null,
      selectedTimesheets: [],
      rejectionReason: "",
      timesheets: [],
      stats: {
        pendingCount: 0,
        approvedThisWeek: { count: 0, hours: 0 },
        awaitingPayroll: { count: 0, hours: 0 }
      },
      pagination: {
        currentPage: 1,
        pageSize: 10,
        totalItems: 0,
        totalPages: 0
      },
      loading: false,
      error: null,
      searchDebounce: null
    };
  },
  computed: {
    filteredTimesheets() {
      return this.timesheets;
    },
    paginatedTimesheets() {
      return this.timesheets;
    },
    isAllSelected() {
      return (
        this.selectedTimesheets.length > 0 &&
        this.selectedTimesheets.length === this.filteredTimesheets.length
      );
    },
    pendingCount() {
      return this.stats.pendingCount;
    },
    approvedThisWeekCount() {
      return this.stats.approvedThisWeek.count;
    },
    approvedHours() {
      return this.stats.approvedThisWeek.hours;
    },
    awaitingPayrollCount() {
      return this.stats.awaitingPayroll.count;
    },
    awaitingPayrollHours() {
      return this.stats.awaitingPayroll.hours;
    },
    dailyBreakdown() {
      if (!this.selectedTimesheet) return [];
      const days = [
        "monday",
        "tuesday",
        "wednesday",
        "thursday",
        "friday",
        "saturday",
        "sunday"
      ];
      const weekStart = this.selectedTimesheet.week_start_date
        ? new Date(this.selectedTimesheet.week_start_date)
        : null;

      // Entries are per-day now, each with its own status and rejection reason
      const entries = new Map(
        (this.selectedTimesheet.days || []).map(day => [
          String(day.work_date).slice(0, 10),
          day
        ])
      );

      return days.map((key, index) => {
        let date = this.selectedTimesheet[`${key}_date`];
        // Fall back to deriving the date from the week start when the
        // per-day column is not set on older records.
        if (!date && weekStart) {
          const derived = new Date(weekStart);
          derived.setDate(derived.getDate() + index);
          date = derived;
        }

        const isoDate = date
          ? new Date(date).toISOString().slice(0, 10)
          : null;
        const entry = isoDate ? entries.get(isoDate) : null;

        return {
          key,
          date,
          entry,
          status: entry ? entry.status : null,
          rejectionReason: entry ? entry.rejection_reason : null,
          hours: entry
            ? Number(entry.hours) || 0
            : Number(this.selectedTimesheet[`${key}_hours`]) || 0
        };
      });
    },
    // Approve/reject act on the days currently awaiting review, not the week
    pendingDayCount() {
      return this.selectedTimesheet
        ? this.selectedTimesheet.pending_days || 0
        : 0;
    }
  },
  watch: {
    statusFilter() {
      this.pagination.currentPage = 1;
      this.loadTimesheets();
    },
    searchQuery() {
      clearTimeout(this.searchDebounce);
      this.searchDebounce = setTimeout(() => {
        this.pagination.currentPage = 1;
        this.loadTimesheets();
      }, 300);
    },
    sortField() {
      this.loadTimesheets();
    },
    sortDirection() {
      this.loadTimesheets();
    }
  },
  async mounted() {
    const userData = localStorage.getItem("user");
    if (!userData) {
      this.$router.push("/");
      return;
    }
    const user = JSON.parse(userData);
    if (user.role !== "admin") {
      this.$router.push("/");
      return;
    }
    
    await Promise.all([
      this.loadStatistics(),
      this.loadTimesheets()
    ]);
  },
  methods: {
    async loadTimesheets() {
      try {
        this.loading = true;
        this.error = null;
        const token = localStorage.getItem('token');
        
        const params = {
          page: this.pagination.currentPage,
          limit: this.pagination.pageSize,
          status: this.statusFilter,
          search: this.searchQuery,
          sortBy: this.sortField,
          sortOrder: this.sortDirection
        };
        
        const response = await axios.get(`${API_URL}/api/admin/timesheets`, {
          headers: { Authorization: `Bearer ${token}` },
          params
        });
        
        if (response.data.success) {
          this.timesheets = response.data.data;
          this.pagination.totalItems = response.data.pagination.total;
          this.pagination.totalPages = response.data.pagination.totalPages;
        }
      } catch (error) {
        console.error('Error loading timesheets:', error);
        this.error = 'Failed to load timesheets';
        if (error.response?.status === 403) {
          alert('Access denied. Admin privileges required.');
          this.$router.push('/');
        }
      } finally {
        this.loading = false;
      }
    },
    
    async loadStatistics() {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API_URL}/api/admin/timesheets/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (response.data.success) {
          this.stats = response.data.data;
        }
      } catch (error) {
        console.error('Error loading statistics:', error);
      }
    },
    
    async approveTimesheet(id) {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.post(
          `${API_URL}/api/admin/timesheets/${id}/approve`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        if (response.data.success) {
          alert('Timesheet approved successfully');
          await Promise.all([
            this.loadTimesheets(),
            this.loadStatistics()
          ]);
        }
      } catch (error) {
        console.error('Error approving timesheet:', error);
        alert(error.response?.data?.message || 'Failed to approve timesheet');
      }
    },
    
    openRejectModal(timesheet) {
      this.selectedTimesheet = timesheet;
      this.rejectionReason = "";
      this.showModal = false;
      this.showRejectModal = true;
    },
    
    async confirmReject() {
      if (!this.rejectionReason || this.rejectionReason.trim() === '') {
        alert('Please provide a rejection reason');
        return;
      }
      
      try {
        const token = localStorage.getItem('token');
        const response = await axios.post(
          `${API_URL}/api/admin/timesheets/${this.selectedTimesheet.id}/reject`,
          { rejection_reason: this.rejectionReason },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        if (response.data.success) {
          alert('Timesheet rejected successfully');
          this.closeRejectModal();
          await Promise.all([
            this.loadTimesheets(),
            this.loadStatistics()
          ]);
        }
      } catch (error) {
        console.error('Error rejecting timesheet:', error);
        alert(error.response?.data?.message || 'Failed to reject timesheet');
      }
    },
    
    async bulkApprove() {
      if (this.selectedTimesheets.length === 0) {
        alert('Please select timesheets to approve');
        return;
      }
      
      if (!confirm(`Are you sure you want to approve ${this.selectedTimesheets.length} timesheet(s)?`)) {
        return;
      }
      
      try {
        const token = localStorage.getItem('token');
        const response = await axios.post(
          `${API_URL}/api/admin/timesheets/bulk-approve`,
          { timesheetIds: this.selectedTimesheets },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        
        if (response.data.success) {
          alert(response.data.message);
          this.selectedTimesheets = [];
          await Promise.all([
            this.loadTimesheets(),
            this.loadStatistics()
          ]);
        }
      } catch (error) {
        console.error('Error bulk approving timesheets:', error);
        alert(error.response?.data?.message || 'Failed to approve timesheets');
      }
    },
    
    toggleSidebar() {
      this.sidebarOpen = !this.sidebarOpen;
    },
    
    sortBy(field) {
      if (this.sortField === field) {
        this.sortDirection = this.sortDirection === "asc" ? "desc" : "asc";
      } else {
        this.sortField = field;
        this.sortDirection = "desc";
      }
    },
    
    toggleSelectAll() {
      if (this.isAllSelected) {
        this.selectedTimesheets = [];
      } else {
        this.selectedTimesheets = this.filteredTimesheets
          .filter(t => t.status === 'pending')
          .map(t => t.id);
      }
    },
    
    toggleSelect(id) {
      const index = this.selectedTimesheets.indexOf(id);
      if (index > -1) {
        this.selectedTimesheets.splice(index, 1);
      } else {
        this.selectedTimesheets.push(id);
      }
    },
    
    viewTimesheet(timesheet) {
      this.selectedTimesheet = timesheet;
      this.showModal = true;
    },
    
    closeModal() {
      this.showModal = false;
      this.selectedTimesheet = null;
    },
    
    closeRejectModal() {
      this.showRejectModal = false;
      this.selectedTimesheet = null;
      this.rejectionReason = "";
    },
    
    formatDate(date) {
      if (!date) return '-';
      return new Date(date).toLocaleDateString("en-GB");
    },
    
    formatWeek(start, end) {
      return `${this.formatDate(start)} - ${this.formatDate(end)}`;
    },
    
    getStatusBadgeClass(status) {
      const classes = {
        draft: "bg-secondary",
        pending: "bg-warning text-dark",
        approved: "bg-success",
        paid: "bg-info",
        rejected: "bg-danger"
      };
      return classes[status] || "bg-secondary";
    },
    
    changePage(page) {
      if (page >= 1 && page <= this.pagination.totalPages) {
        this.pagination.currentPage = page;
        this.loadTimesheets();
      }
    },
    
    handleLogout() {
      localStorage.removeItem("user");
      localStorage.removeItem("token");
      this.$router.push("/");
    },
  },
};
</script>

<style scoped>
.dashboard-container {
  min-height: 100vh;
  background-color: #f8f9fa;
}

/* Timesheet details modal */
.detail-label {
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #6c757d;
  margin-bottom: 0.15rem;
}

.detail-value {
  font-size: 0.95rem;
  color: #2c3e50;
}

.breakdown-table th {
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #6c757d;
}

/* Sidebar Styles */
.sidebar {
  width: 280px;
  background-color: #2c3e50;
  color: white;
  min-height: 100vh;
  position: fixed;
  left: 0;
  top: 0;
  overflow-y: auto;
  transition: all 0.3s ease;
  z-index: 1000;
}

.sidebar.collapsed {
  width: 70px;
}

.sidebar-header {
  padding: 2rem 1.5rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  transition: padding 0.3s ease;
}

.sidebar.collapsed .sidebar-header {
  padding: 2rem 0.5rem;
}

.sidebar-header h4 {
  color: white;
  font-weight: 600;
  font-size: 1.5rem;
  white-space: nowrap;
  overflow: hidden;
}

.sidebar-nav {
  padding: 1rem 0;
}

.nav-item {
  display: flex;
  align-items: center;
  padding: 1rem 1.5rem;
  color: rgba(255, 255, 255, 0.8);
  text-decoration: none;
  transition: all 0.3s ease;
  font-size: 1.05rem;
  gap: 1rem;
  white-space: nowrap;
  overflow: hidden;
}

.sidebar.collapsed .nav-item {
  padding: 1rem 0.5rem;
  justify-content: center;
  font-size: 0;
}

.sidebar.collapsed .nav-item svg {
  margin: 0;
}

.nav-item:hover {
  background-color: rgba(255, 255, 255, 0.1);
  color: white;
}

.nav-item.active {
  background-color: rgba(255, 255, 255, 0.15);
  color: white;
  border-left: 4px solid #667eea;
}

.nav-item svg {
  flex-shrink: 0;
}

/* Main Content */
.main-content {
  margin-left: 280px;
  min-height: 100vh;
  background-color: #f8f9fa;
  transition: margin-left 0.3s ease;
}

.main-content.sidebar-collapsed {
  margin-left: 70px;
}

.navbar {
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

/* Statistics Cards */
.stat-card {
  transition: transform 0.2s;
}

.stat-card:hover {
  transform: translateY(-2px);
}

.stat-icon {
  width: 50px;
  height: 50px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
}

/* Table */
.table th {
  font-weight: 600;
  text-transform: uppercase;
  font-size: 0.85rem;
  letter-spacing: 0.5px;
  color: #6c757d;
}

.sortable {
  cursor: pointer;
  user-select: none;
}

.sortable:hover {
  background-color: #e9ecef !important;
}

.modal.show {
  background-color: rgba(0, 0, 0, 0.5);
}

/* Responsive */
@media (max-width: 768px) {
  .sidebar {
    transform: translateX(-100%);
  }

  .sidebar:not(.collapsed) {
    transform: translateX(0);
  }

  .main-content {
    margin-left: 0 !important;
  }
}
</style>
